import type {
  BoardDto,
  BoardSnapshot,
  BoardSummaryDto,
  MemberDto,
  UploadDto,
  UserDto,
  VersionDto,
} from '@miroom/shared';
import type { AuthApi, BoardsApi, VerifiedLink } from '../../application/ports/api';
import { HttpClient } from './httpClient';

export class HttpAuthApi implements AuthApi {
  constructor(private readonly http = new HttpClient()) {}

  async me() {
    return (await this.http.request<{ user: UserDto | null }>('GET', '/api/me')).user;
  }
  async requestLink(input: Parameters<AuthApi['requestLink']>[0]) {
    await this.http.request<{ sent: boolean }>('POST', '/api/auth/link', input);
  }
  verifyLink(token: string) {
    return this.http.request<VerifiedLink>('POST', '/api/auth/verify', { token });
  }
  logout() {
    return this.http.request<void>('POST', '/api/auth/logout');
  }
  logoutAll() {
    return this.http.request<void>('POST', '/api/auth/logout-all');
  }
  async updateProfile(patch: Parameters<AuthApi['updateProfile']>[0]) {
    return (await this.http.request<{ user: UserDto }>('PATCH', '/api/me', patch)).user;
  }
  deleteAccount(email: string) {
    return this.http.request<void>('DELETE', '/api/me', { email });
  }
}

const b = (id: string) => `/api/boards/${encodeURIComponent(id)}`;

export class HttpBoardsApi implements BoardsApi {
  constructor(private readonly http = new HttpClient()) {}

  async list(...[filter, search, sort]: Parameters<BoardsApi['list']>) {
    const q = new URLSearchParams({
      filter,
      ...(search ? { q: search } : {}),
      ...(sort && sort !== 'date' ? { sort } : {}),
    });
    return (await this.http.request<{ boards: BoardSummaryDto[] }>('GET', `/api/boards?${q}`))
      .boards;
  }
  async create(input: Parameters<BoardsApi['create']>[0]) {
    return (await this.http.request<{ board: BoardDto }>('POST', '/api/boards', input)).board;
  }
  async get(id: string) {
    return (await this.http.request<{ board: BoardDto }>('GET', b(id))).board;
  }
  async update(id: string, patch: Parameters<BoardsApi['update']>[1]) {
    return (await this.http.request<{ board: BoardDto }>('PATCH', b(id), patch)).board;
  }
  remove(id: string) {
    return this.http.request<void>('DELETE', b(id));
  }
  async restore(id: string) {
    return (await this.http.request<{ board: BoardDto }>('POST', `${b(id)}/restore`)).board;
  }
  destroy(id: string) {
    return this.http.request<void>('DELETE', `${b(id)}/permanent`);
  }
  async duplicate(id: string) {
    return (await this.http.request<{ board: BoardDto }>('POST', `${b(id)}/duplicate`)).board;
  }
  setStarred(id: string, starred: boolean) {
    return this.http.request<void>('PUT', `${b(id)}/star`, { starred });
  }
  setThumbnail(id: string, dataUrl: string) {
    return this.http.request<void>('PUT', `${b(id)}/thumbnail`, { dataUrl });
  }
  async listMembers(id: string) {
    return (await this.http.request<{ members: MemberDto[] }>('GET', `${b(id)}/members`)).members;
  }
  async addMember(id: string, input: Parameters<BoardsApi['addMember']>[1]) {
    return (await this.http.request<{ member: MemberDto }>('POST', `${b(id)}/members`, input))
      .member;
  }
  async updateMember(id: string, userId: string, role: Parameters<BoardsApi['updateMember']>[2]) {
    return (
      await this.http.request<{ member: MemberDto }>('PATCH', `${b(id)}/members/${userId}`, {
        role,
      })
    ).member;
  }
  removeMember(id: string, userId: string) {
    return this.http.request<void>('DELETE', `${b(id)}/members/${userId}`);
  }
  async listVersions(id: string) {
    return (await this.http.request<{ versions: VersionDto[] }>('GET', `${b(id)}/versions`))
      .versions;
  }
  async createVersion(id: string, name: string) {
    return (await this.http.request<{ version: VersionDto }>('POST', `${b(id)}/versions`, { name }))
      .version;
  }
  async getVersion(id: string, versionId: string) {
    return (
      await this.http.request<{ snapshot: BoardSnapshot }>('GET', `${b(id)}/versions/${versionId}`)
    ).snapshot;
  }
  restoreVersion(id: string, versionId: string) {
    return this.http.request<void>('POST', `${b(id)}/versions/${versionId}/restore`);
  }
  async upload(id: string, file: Blob, onProgress?: (f: number) => void) {
    return (await this.http.upload<{ upload: UploadDto }>(`${b(id)}/uploads`, file, onProgress))
      .upload;
  }
  async copyUploads(id: string, urls: string[]) {
    return (
      await this.http.request<{ urls: Record<string, string> }>('POST', `${b(id)}/uploads/copy`, {
        urls,
      })
    ).urls;
  }
  exportJson(id: string) {
    return this.http.request<BoardSnapshot>('GET', `${b(id)}/export.json`);
  }
  async importJson(snapshot: unknown, title?: string) {
    return (
      await this.http.request<{ board: BoardDto }>('POST', '/api/boards/import', {
        snapshot,
        title,
      })
    ).board;
  }
}
